import UptimeGraph from "./UptimeGraph";
import TestAlertButton from "./TestAlertButton";
import { StatusBadge } from "./badges";
import { timeAgo } from "./statusFormat";
import type { StatusSummary, UptimeGraphData } from "../../types/status";

const IS_QA = import.meta.env.VITE_APP_ENV !== "production";

const OVERALL_CONFIG: Record<string, { label: string; bg: string; border: string; text: string }> = {
  operational: { label: "All systems operational", bg: "bg-green-50", border: "border-green-200", text: "text-green-800" },
  degraded: { label: "Partial degradation", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800" },
  outage: { label: "Service disruption", bg: "bg-red-50", border: "border-red-200", text: "text-red-800" },
};

export default function OverviewTab({
  data,
  uptimeGraph,
  uptimeLoading,
  onDowntimePeriodClick,
  sourceLabel,
}: {
  data: StatusSummary | null;
  uptimeGraph: UptimeGraphData | null;
  uptimeLoading: boolean;
  onDowntimePeriodClick: (ids: string[]) => void;
  sourceLabel: string;
}) {
  const overall = OVERALL_CONFIG[data?.overall ?? ""] ?? OVERALL_CONFIG.operational;
  return (
    <>
      <UptimeGraph
        graphData={uptimeGraph}
        loading={uptimeLoading}
        onDowntimePeriodClick={onDowntimePeriodClick}
        sourceLabel={sourceLabel}
      />

      <div className={`mb-8 rounded-xl border px-5 py-4 ${overall.bg} ${overall.border}`}>
        <p className={`text-sm font-semibold ${overall.text}`}>{overall.label}</p>
        {data?.integrations?.[0]?.last_checked_at && (
          <p className="mt-0.5 text-xs text-gray-500">
            Last checked {timeAgo(data.integrations[0].last_checked_at)}
          </p>
        )}
      </div>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Integrations</h2>
        <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
          {data?.integrations?.map((item) => (
            <div key={item.service_name} className="flex items-center justify-between gap-4 px-5 py-3.5">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-medium text-gray-800">{item.display_name}</span>
                {item.response_time_ms != null && (
                  <span className="text-xs text-gray-400">{item.response_time_ms}ms</span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {item.error_message && (
                  <span className="hidden max-w-xs truncate text-xs text-red-500 sm:block" title={item.error_message}>
                    {item.error_message}
                  </span>
                )}
                {IS_QA && <TestAlertButton serviceName={item.display_name} serviceType="integration" />}
                <StatusBadge status={item.status} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
          Monitored services {(data?.urls?.length ?? 0) > 0 && `(${data?.urls?.length})`}
        </h2>
        {!data?.urls?.length ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-5 py-8 text-center">
            <p className="text-sm text-gray-400">No monitored URLs configured.</p>
            <p className="mt-1 text-xs text-gray-400">Admins can add endpoints in the admin panel under Status settings.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
            {data.urls.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-4 px-5 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800">{item.name}</p>
                  <p className="truncate text-xs text-gray-400">{item.url}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {item.response_time_ms != null && (
                    <span className="text-xs text-gray-400">{item.response_time_ms}ms</span>
                  )}
                  {item.last_checked_at && (
                    <span className="hidden text-xs text-gray-400 sm:block">{timeAgo(item.last_checked_at)}</span>
                  )}
                  {IS_QA && <TestAlertButton serviceName={item.name} serviceType="url" />}
                  <StatusBadge status={item.status ?? "unknown"} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
